import { Form, FormField } from "@babackapp/ui/components/form";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import useTranslation from "@babackapp/translations/use-translation";
import {
  CreateOneWorkflowDto,
  WorkflowDto,
} from "@babackapp/private-api-client/types";
import { Button } from "@babackapp/ui/components/button";
import { v4 as uuidv4 } from "uuid";
import FormInput from "@babackapp/react-hook-forms-components/form-input";
import FormInputRow from "@babackapp/react-hook-forms-components/form-input-row";

const formSchema = z.object({
  name: z.string(),
  description: z.string(),
  status: z.enum(["ACTIVE", "INACTIVE"]),
  steps: z.array(z.any()),
  trigger: z.object({
    id: z.string(),
    type: z.enum(["TRIGGER", "ACTION"]),
    ressource: z.string(),
    operation: z.string(),
    parameters: z.object({
      status: z.array(z.string()),
      resolution: z.array(z.string()),
      reason: z.array(z.string()),
      users: z.array(z.string()),
      tags: z.string().nullable(),
      customFields: z.array(z.any()),
      note: z.string().nullable(),
    }),
  }),
});

type CreateOneWorkflowFormValues = z.infer<typeof formSchema>;

type CreateOneWorkflowProps = {
  defaultValues?: z.infer<typeof formSchema>;
  onSubmit: (data: CreateOneWorkflowDto) => Promise<WorkflowDto>;
  onCloseForm: () => void;
};

const DEFAULT_VALUES: CreateOneWorkflowFormValues = {
  name: "",
  description: "",
  status: "INACTIVE",
  steps: [],
  trigger: {
    id: uuidv4(),
    type: "TRIGGER",
    ressource: "returnRequest",
    operation: "returnRequest:returnRequest:create",
    parameters: {
      status: [],
      resolution: [],
      reason: [],
      users: [],
      tags: null,
      customFields: [],
      note: null,
    },
  },
};

export default function AddWorkflowForm({
  defaultValues = DEFAULT_VALUES,
  onSubmit,
  onCloseForm,
}: CreateOneWorkflowProps) {
  const t = useTranslation();

  const form = useForm<CreateOneWorkflowFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  const _onSubmit = form.handleSubmit((data) => {
    onSubmit(data);
  });

  return (
    <Form {...form}>
      <form
        onSubmit={_onSubmit}
        className="flex flex-col justify-start gap-8 mt-[16px]"
      >
        <FormInputRow>
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormInput label={t("workflow.create.name")} {...field} />
            )}
          />
          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormInput label={t("workflow.create.description")} {...field} />
            )}
          />
        </FormInputRow>

        <div className={"flex items-center gap-2 justify-center"}>
          <Button className="w-1/2" variant="outline" onClick={onCloseForm}>
            {t("misc.cancel")}
          </Button>
          <Button className="w-1/2" disabled={!form.formState.isDirty}>
            {t("workflow.create.create")}
          </Button>
        </div>
      </form>
    </Form>
  );
}
